'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button, Modal, TextInput, Textarea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useAuthValidation } from '@/src/lib/hooks/useAuthValidation';
import { Editor } from '../EditorText/Editor';
import { FullSizeLoader } from '../Loader/FullSizeLoader';
import { Dropzone } from '../EditorText/Dropzone';
import { authValidation } from '@/src/helpers/authValidation';

export const EditionModale = ({
  content,
  id,
  title: initialTitle,
  description: initialDescription,
  mainImage: initialMainImage,
}: {
  content: any;
  id: string;
  title: string;
  description: string;
  mainImage: string;
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [editorContent, setEditorContent] = useState<any>(content);
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [mainImage, setMainImage] = useState<string | null>(initialMainImage);
  const [mainImageError, setMainImageError] = useState<string | undefined>(undefined);
  const [disconnected, setDisconnected] = useState(false);
  const [validated, setValidated] = useState(false);

  const router = useRouter();

  const handleOpenClick = async (value: boolean) => {
    const isValid = await authValidation();
    if (!isValid) {
      setDisconnected(true); // show modal
    } else {
      setValidated(true);
    }
    open()
  }

  const saveArticle = async (id: string) => {
    const response = await fetch(`/api/articles/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content: editorContent,
        title,
        description,
        mainImage,
      }),
    });

    if (!response.ok) {
      console.error('❌ Failed to save article.');
    } else {
      console.log('✅ Article updated successfully.');
      close();
      router.refresh();
    }
  };

  const deleteArticle = async (id: string) => {
    const response = await fetch(`/api/articles/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      console.error('❌ Failed to delete article.');
      response.status === 401 && router.push('/login');
    } else {
      console.log('🗑️ Article deleted successfully.');
      router.push('/articles');
    }
  };

  return (
    <>
      <Button onClick={() => handleOpenClick} color="green" style={{ marginRight: 10, marginLeft: 60 }}>
        Modifier l'article
      </Button>
      <Button onClick={() => deleteArticle(id)} color="red">
        Supprimer l'article
      </Button>

      <>
        <Modal
          opened={disconnected}
          onClose={() => {
            router.push('/login');
          }}
          title="Session Expired"
          centered
        >
          <p>Your session has expired. Please log in again.</p>
          <Button onClick={() => router.push('/login')}>Go to Login</Button>
        </Modal>
      </>

      <Modal opened={opened} onClose={close} title="Modifier l'article" size="lg">
        <TextInput
          label="Titre"
          placeholder="Titre de l'article"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          withAsterisk
          mb="md"
        />
        <Textarea
          label="Description"
          placeholder="Courte description"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          autosize
          minRows={2}
          mb="md"
        />

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}>
            Image principale
          </label>
          <Dropzone
            setImageUrl={setMainImage}
            imageUrl={mainImage}
            error={mainImageError}
          />
        </div>

        {/* @ts-ignore */}
        <Editor content={editorContent} setEditorContent={setEditorContent} />

        <Button onClick={() => saveArticle(id)} color="green" fullWidth mt="md">
          Sauvegarder les modifications
        </Button>
      </Modal>
    </>
  );
};
